"use client"

import type React from "react"
import { useState } from "react"
import type { ConnectorConfig, ConnectionStatus, SetupData } from "../SetupFlow"
import "./ConnectAccounts.css"

interface ConnectAccountsProps {
  selectedConnector: ConnectorConfig
  onSubmit: (status: ConnectionStatus) => void
  onBack: () => void
  setupData: SetupData
}

const ConnectAccounts: React.FC<ConnectAccountsProps> = ({ selectedConnector, onSubmit, onBack, setupData }) => {
  const [hubspotConnected, setHubspotConnected] = useState(setupData.connectionStatus.hubspot)
  const [salesforceConnected, setSalesforceConnected] = useState(setupData.connectionStatus.salesforce)

  const handleHubspotConnect = () => {
    setHubspotConnected(true)
  }

  const handleSalesforceConnect = () => {
    if (!hubspotConnected) return
    setSalesforceConnected(true)
  }

  const handleSubmit = () => {
    if (hubspotConnected && salesforceConnected) {
      onSubmit({
        hubspot: hubspotConnected,
        salesforce: salesforceConnected,
      })
    }
  }

  return (
    <div className="connect-accounts">
      <div className="connect-accounts-header">
        <h1>Connect Your Accounts</h1>
        <p>
          Authorize access to your {selectedConnector.to} and {selectedConnector.from} accounts to keep your data in
          sync
        </p>
      </div>

      <div className="platforms-section">
        <div className="platform-card">
          <div className="platform-logo">
            <img src={selectedConnector.fromLogo || "/placeholder.svg"} alt={selectedConnector.from} />
          </div>
          <button
            className={`connect-button ${hubspotConnected ? "connected" : "primary"}`}
            onClick={handleHubspotConnect}
            disabled={hubspotConnected}
          >
            {hubspotConnected ? "Connected" : "Connect"}
          </button>
          <div className="status">Status: {hubspotConnected ? "Connected" : "Not Connected"}</div>
        </div>

        <div className="sync-icon">
          <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 112.62 120.72"><title>circle arrow</title><path d="M11.64,100.12l-.4-.47-1.06,8.63a5.08,5.08,0,0,1-1.92,3.41A5.11,5.11,0,0,1,0,107L2.79,84.65v-.07a3.28,3.28,0,0,1,.08-.41h0A5.09,5.09,0,0,1,9,80.39q11.22,2.53,22.42,5.15a5,5,0,0,1,3.17,2.25,5.14,5.14,0,0,1,.64,3.84v0a5,5,0,0,1-2.25,3.16,5.08,5.08,0,0,1-3.83.65c-3.31-.75-6.62-1.52-9.92-2.28a40.71,40.71,0,0,0,2.84,3,50.09,50.09,0,0,0,26.23,13.49,48.67,48.67,0,0,0,14.71.34A47.35,47.35,0,0,0,77,106h0q2.52-1.19,4.83-2.54c1.56-.93,3.07-1.92,4.51-3a50.8,50.8,0,0,0,8.56-7.88,48.92,48.92,0,0,0,6.39-9.45l.56-1.1,10,2.69-.8,1.66a58.64,58.64,0,0,1-7.9,12.24,61.28,61.28,0,0,1-10.81,10.1c-1.68,1.23-3.46,2.4-5.32,3.5s-3.73,2.07-5.74,3a58,58,0,0,1-17,5,58.56,58.56,0,0,1-17.79-.39,60.21,60.21,0,0,1-31.58-16.26c-1.2-1.16-2.26-2.31-3.24-3.45ZM101,20.6l.4.47,1-8.63a5.11,5.11,0,1,1,10.14,1.26l-2.74,22.37,0,.07c0,.13,0,.27-.07.41h0a5.09,5.09,0,0,1-6.08,3.78c-7.47-1.69-15-3.4-22.42-5.15a5,5,0,0,1-3.16-2.25,5.1,5.1,0,0,1-.65-3.84v0a5,5,0,0,1,2.25-3.16,5.1,5.1,0,0,1,3.84-.65c3.31.75,6.61,1.52,9.92,2.28-.84-1-1.77-2-2.84-3.05a50.09,50.09,0,0,0-12.13-8.73A49.49,49.49,0,0,0,64.37,11a48.6,48.6,0,0,0-14.7-.34,47.26,47.26,0,0,0-14,4.1h0q-2.53,1.18-4.83,2.54c-1.57.93-3.07,1.92-4.52,3a50.34,50.34,0,0,0-8.55,7.88,48,48,0,0,0-6.39,9.45l-.57,1.1L.76,36l.8-1.66A58.9,58.9,0,0,1,9.46,22.1,61.63,61.63,0,0,1,20.27,12q2.54-1.85,5.32-3.5c1.81-1.06,3.73-2.07,5.74-3a58,58,0,0,1,17-5A58.56,58.56,0,0,1,66.16.89a59.77,59.77,0,0,1,17,5.74A60.4,60.4,0,0,1,97.75,17.15c1.19,1.16,2.26,2.31,3.24,3.45Z"/></svg>
          {/* <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
            <path d="M23 4v6h-6" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 2.12 9.36L1 14" />
          </svg> */}
        </div>

        <div className="platform-card">
          <div className="platform-logo">
            <img src={selectedConnector.toLogo || "/placeholder.svg"} alt={selectedConnector.to} />
          </div>
          <button
            className={`connect-button ${
              salesforceConnected ? "connected" : hubspotConnected ? "primary" : "disabled"
            }`}
            onClick={handleSalesforceConnect}
            disabled={!hubspotConnected || salesforceConnected}
          >
            {salesforceConnected ? "Connected" : "Connect"}
          </button>
          <div className="status">Status: {salesforceConnected ? "Connected" : "Not Connected"}</div>
        </div>
      </div>

      <div className="note-section">
        <div className="note">
          <span className="note-label">Note ⚠️</span>
          <span>
            : Connect {selectedConnector.from} first and then {selectedConnector.to} to continue.
          </span>
        </div>
      </div>

      <div className="features-section">
        <h2>What You Can Do With NetzSync</h2>
        <div className="features-list">
          <div className="feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <path d="M23 4v6h-6" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </div>
            <span>
              Sync contacts, tasks, and tickets between {selectedConnector.to} and {selectedConnector.from}
            </span>
          </div>
          <div className="feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
            <span>Configure uni or bi-directional sync</span>
          </div>
          <div className="feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
              </svg>
            </div>
            <span>Track sync status and view logs</span>
          </div>
        </div>
      </div>

      <div className="actions-section">
        <button className="back-button" onClick={onBack}>
          Back
        </button>
        <button className="next-button" onClick={handleSubmit} disabled={!hubspotConnected || !salesforceConnected}>
          Next
        </button>
      </div>
    </div>
  )
}

export default ConnectAccounts
